import type { ReactNode } from "react";
import { Link } from "react-router";
import { AlertTriangle, Check } from "lucide-react";
import type { ReplenishmentProgress } from "@shared-core/data/api-types";
import {
  formatCount,
  formatDateOrDash,
  formatDateTimeOrDash,
} from "@shared-core/domain/formatters";
import {
  formatUnitCostPence,
  progressSteps,
  type ProgressStep,
} from "@shared-core/domain/stock-replenishment";
import { paths } from "@shared-core/routing/routes";
import { Button } from "../../app/components/primitives/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../app/components/primitives/card";

/**
 * A raised order's progress to goods-in, read from the order, the purchase
 * order, the receipts and stock's booking. The page writes nothing here: the
 * links go to the owning pages (the order, Purchasing, Record received).
 */
export function ReplenishmentProgressCard({
  progress,
  raisedAt,
  raisedByName,
}: {
  progress: ReplenishmentProgress;
  raisedAt: string | null;
  raisedByName: string | null;
}) {
  const steps = progressSteps(
    progress,
    { at: raisedAt, byName: raisedByName },
    formatCount,
  );
  const stage = progress.stage;
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 flex-wrap">
        <CardTitle>Progress</CardTitle>
        <p className="text-xs text-muted-foreground">
          Read from the order, purchase order and stock. Updated as each
          happens.
        </p>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        {stage === "waiting_on_credit" && (
          <Banner tone="warn">
            The customer is on credit hold, so the order is parked until finance
            lifts it.
          </Banner>
        )}
        {stage === "arrived_not_in_stock" && (
          <Banner tone="warn">
            {formatCount(progress.received_quantity)} were recorded as received
            {progress.arrived_on
              ? ` on ${formatDateOrDash(progress.arrived_on)}`
              : ""}{" "}
            but stock has not booked them in yet. The system retries every few
            minutes; if this stays, tell stock control.
          </Banner>
        )}
        {stage === "in_stock" && (
          <Banner tone="ok">
            Done. {formatCount(progress.received_quantity)} are in stock
            {progress.warehouse_name ? ` at ${progress.warehouse_name}` : ""}
            {progress.batch_no == null
              ? ""
              : `, batch ${progress.batch_no}${progress.unit_cost_pence == null ? "" : ` at ${formatUnitCostPence(progress.unit_cost_pence)} a unit`}`}
            .
          </Banner>
        )}

        <ol className="divide-y divide-border">
          {steps.map((step) => (
            <Step key={step.key} step={step} />
          ))}
        </ol>

        <div className="flex flex-wrap items-center gap-2">
          <Button asChild variant="outline">
            <Link to={paths.orderDetail({ orderNo: progress.order_no })}>
              Open the order
            </Link>
          </Button>
          {(stage === "placed" || stage === "despatched") && (
            <Button asChild variant="outline">
              <Link to={paths.orderDetail({ orderNo: progress.order_no })}>
                Record received…
              </Link>
            </Button>
          )}
          {stage === "to_place" && (
            <Button asChild variant="outline">
              <Link
                to={
                  progress.purchase_order_no
                    ? paths.placement({
                        purchaseOrderNo: progress.purchase_order_no,
                      })
                    : paths.purchasing()
                }
              >
                Open To place
              </Link>
            </Button>
          )}
        </div>
        {(stage === "waiting_on_credit" ||
          stage === "waiting_to_release" ||
          stage === "to_place" ||
          stage === "placed") && (
          <p className="text-xs text-muted-foreground">
            Cancelling is on the order line, in Orders; the next sweep proposes
            again if the item is still low.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function Banner({
  tone,
  children,
}: {
  tone: "warn" | "ok";
  children: ReactNode;
}) {
  return (
    <div
      role={tone === "warn" ? "alert" : "status"}
      className={
        tone === "warn"
          ? "flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-900 dark:bg-amber-900/20 dark:border-amber-800 dark:text-amber-200"
          : "flex items-start gap-2 rounded-lg border border-green-300 bg-green-50 p-3 text-green-800 dark:bg-green-900/20 dark:border-green-800 dark:text-green-200"
      }
    >
      {tone === "warn" ? (
        <AlertTriangle className="size-4 shrink-0 mt-0.5" />
      ) : (
        <Check className="size-4 shrink-0 mt-0.5" />
      )}
      <span>{children}</span>
    </div>
  );
}

const DOT: Record<ProgressStep["state"], string> = {
  done: "bg-green-600 border-green-600",
  now: "border-primary",
  problem: "bg-amber-600 border-amber-600",
};

function Step({ step }: { step: ProgressStep }) {
  return (
    <li className="grid grid-cols-[22px_1fr_auto] gap-2.5 py-2.5 items-start">
      <span
        aria-hidden="true"
        className={`mt-1 size-3.5 rounded-full border-2 ${DOT[step.state]}`}
      />
      <div>
        <div>
          <span className="sr-only">
            {step.state === "done"
              ? "Done: "
              : step.state === "problem"
                ? "Needs attention: "
                : "Now: "}
          </span>
          <span>{step.title}</span>
        </div>
        {step.detail && (
          <div className="text-xs text-muted-foreground">{step.detail}</div>
        )}
      </div>
      <span className="text-xs text-muted-foreground whitespace-nowrap">
        {step.when ? formatWhen(step.when) : ""}
      </span>
    </li>
  );
}

/** A calendar date or a timestamp, as the step recorded it. */
function formatWhen(value: string): string {
  return value.length > 10
    ? formatDateTimeOrDash(value)
    : formatDateOrDash(value);
}
